/* React */
import { useReducer } from 'react'
import reducer, { initialState, betreffs } from './reducer'

/* Material */
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import Switch from '@mui/material/Switch'
import Button from '@mui/material/Button'
import useStyles from './styles'

/* Validatoren */
import * as EmailValidator from 'email-validator'
import { isValidTelefon } from 'src/functions/validators'

/* Functions */
import { post } from 'src/functions/fetch'
import getRecaptchaToken from 'src/functions/getRecaptchaToken'

/* Options */
import config from 'src/config'

/* Components */
import Loading from 'src/components/Loading'
import Gesendet from './Gesendet'
import Error from './Error'
import DsgvoText from './DsgvoText'
import GoogleRecaptchaInfos from 'src/components/GoogleRecaptchaInfos'
import { pageBuilderButtonSx } from 'src/theme/pageBuilderButtons'

const Kontaktformular: React.FC = () => {
  const classes = useStyles()

  const [
    { 
      betreff, email, telefon, message,
      state, dsgvo,
    },
    dispatch,
  ] = useReducer(reducer, initialState)

  const emailIsValid = EmailValidator.validate(email)

  const telefonForm = betreff === 'rueckruf' || betreff === 'termin'
  const telefonIsValid = isValidTelefon(telefon)

  const handleChange = (
    {target:{name, value}}: React.ChangeEvent<HTMLInputElement>
  ) =>
    dispatch({
      type: 'CHANGE_INPUT',
      payload: { name, value },
    })

  const handleSubmit = async () => {

    dispatch({ type: 'CHANGE_STATE', payload: 'loading' })
    
    if (
         !emailIsValid
      || ( telefonForm && telefonIsValid !== true )
      || !dsgvo
      || betreff === ''
      || message === ''
    ) {
      dispatch({ type: 'CHANGE_STATE', payload: 'tryed' })
      return
    }

    const recaptchaToken = await getRecaptchaToken('contact_form')
    const res = await post({
      url: 'kontakt',
      body: {
        betreff,
        email,
        telefon,
        message,
        shop: config.shop.shop,
        recaptchaToken,
      }
    })

    if (!res) {
      dispatch({ type: 'CHANGE_STATE', payload: 'error' })
      return
    }

    dispatch({ type: 'CHANGE_STATE', payload: 'success' })
  }

  if (state === 'loading') return <Loading />
  
  if (state === 'error') return <Error 
    retry={()=>dispatch({ type: 'CHANGE_STATE', payload: 'tryed' })} 
  />

  if (state === 'success') return <Gesendet 
    betreff={betreffs.find(({value}) => value === betreff)?.label||''}
    {...{ email, message }} 
  />

  return <form 
    id='form-kontakt' 
    className={classes.kontaktformular}
    onSubmit={e=>{
      e.preventDefault()
      handleSubmit()
    }}
  >

    <div className={classes.contactWrapper}>
      <TextField
        id='betreff' name='betreff' label='Betreff'
        variant='outlined' select fullWidth required
        value={betreff} onChange={handleChange}
        error={ state==='tryed' && betreff === '' }
        helperText={(
          state==='tryed' && betreff === '' &&
            'Bitte wählen Sie einen Betreff aus.'
        )}
      >
        {betreffs.map(({ value, label }) => (
          <MenuItem key={value} value={value}>
            {label}
          </MenuItem>
        ))}
      </TextField>
      
      <TextField
        id='email' name='email' label='Email'
        variant='outlined' fullWidth required
        value={email} onChange={handleChange}
        error={ state==='tryed' && !emailIsValid }
        helperText={
          state==='tryed' && !emailIsValid &&
            'Bitte geben Sie eine gültige Email-Adresse ein.'
        }
      />
    </div>

    <div className={classes.messageWrapper}>

      { telefonForm &&
        <div className={classes.telefonWrapper}>
          <TextField
            id='phone' name='telefon' label='Telefon-Nummer'
            variant='outlined' fullWidth required
            value={telefon} onChange={handleChange}
            error={ state==='tryed' && typeof telefonIsValid === 'string' }
            helperText={
              state==='tryed' && typeof telefonIsValid === 'string' &&
                telefonIsValid
            }
          />
        </div>
      }

      <TextField
        id='message' name='message' label={telefonForm ? 'Ihr Anliegen' : 'Ihre Nachricht'}
        className={classes.messageField}
        variant='outlined' multiline fullWidth 
        required rows={!telefonForm ? 14 : 11}
        value={message} onChange={handleChange}
        error={ state==='tryed' && message === '' }
        helperText={
          state==='tryed' && message === '' &&
            'Bitte geben Sie Ihre Nachricht mit an.'
        }
      />
    </div>

    <div className={classes.buttonWrapper}>
      <Button 
        size='large' variant='contained' fullWidth color='primary'
        sx={pageBuilderButtonSx}
        type='submit'
      >
        Kontaktformular absenden
      </Button>
    </div>



    <div className={classes.dsgvoWrapper}>
      <div className={classes.dsgvoForm}>
        <Switch 
          inputProps={{ 'aria-label': 'DHL DSGVO' }}
          checked={dsgvo}
          onChange={()=>dispatch({ type: 'DSGVO_TOGGLE', payload: !dsgvo })}
        /> 
        <DsgvoText />
          {state==='tryed' && !dsgvo && <>
            <div></div>
            <p className={classes.dsgvoError}>
              Bitte akzeptieren Sie die Datenschutzbestimmungen.
            </p>
          </>}
      </div>
      <div className={classes.dsgvoRecaptcha}>
        <GoogleRecaptchaInfos />
      </div>
    </div>

  </form>
}

export default Kontaktformular
